import { useEffect, useState } from 'react';
import { useBodyClass } from '@/hooks/useBodyClass';
import { X, Sparkles, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { NutrientsData, NutrientItem } from '@/hooks/useNutrients';
import { useUserStore } from '@/store/userStore';
import { calculatePercent, getLevelByPercent } from '@/utils/dailyNorms';

interface Props {
  open: boolean;
  onClose: () => void;
  mealName?: string;
  data: NutrientsData | null;
  loading: boolean;
  /** Перезапросить данные. Возвращает свежий ответ или null. */
  onRetry?: () => Promise<any>;
  /** Пнуть досчёт микронутриентов, если он ещё не проходил. */
  onKickEnrich?: () => void;
}

const LEVEL_COLORS = {
  normal: 'text-ink-muted bg-bg-elevated',
  good: 'text-emerald-glow bg-emerald-glow/10',
  excellent: 'text-emerald-glow bg-emerald-glow/15 font-semibold',
  very_high: 'text-warn bg-warn/15 font-bold',
};

const LEVEL_LABELS = {
  normal: 'Норма',
  good: 'Хороший источник',
  excellent: 'Отличный источник',
  very_high: 'Очень много',
};

const MACRO_NAMES = ['белок', 'протеин', 'белки', 'жиры', 'жир', 'углеводы', 'углевод'];
function isMacro(name: string): boolean {
  return MACRO_NAMES.includes(name.toLowerCase().trim());
}

export function NutrientsModal({ open, onClose, mealName, data, loading, onRetry, onKickEnrich }: Props) {
  // Экраны лежат в слое ниже нижней панели (z-10 против z-30): без этого панель
  // закрывает низ выезжающего окна вместе с его кнопками (боевой случай 10.10.2026).
  useBodyClass('modal-open', open);
  // Досчёт микронутриентов идёт ПОСЛЕ сохранения еды — ради скорости распознавания.
  // Если человек открыл «Пользу» раньше, чем он закончился, показываем ожидание
  // и сами обновляемся. Раньше здесь висела мёртвая надпись, а ниже одновременно
  // стояло «значимых нутриентов не выделено» — два противоречия и ноль движения.
  const pending = !!data?.pending;
  const [waited, setWaited] = useState(0);

  useEffect(() => {
    if (!open || !pending) {
      setWaited(0);
      return;
    }
    onKickEnrich?.(); // подтолкнуть досчёт, вдруг до него ещё не дошли руки
    let stop = false;
    const tick = async () => {
      if (stop) return;
      const fresh = await onRetry?.();
      if (!stop && fresh?.pending !== false) setWaited((w: number) => w + 1);
    };
    const t = setInterval(tick, 5000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [open, pending, onRetry, onKickEnrich]);

  const { profile } = useUserStore();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  // Перерасчёт элементов с учётом профиля
  function enrichItem(item: NutrientItem): NutrientItem & {
    normValue?: number;
    sourceLabel?: string;
    isMacro: boolean;
  } {
    if (!profile) return { ...item, isMacro: isMacro(item.name) };

    const result = calculatePercent(item.name, item.amount, profile);
    if (!result) return { ...item, isMacro: isMacro(item.name) };

    return {
      ...item,
      daily_percent: result.percent,
      level: getLevelByPercent(result.percent),
      normValue: result.norm,
      sourceLabel: result.source,
      isMacro: isMacro(item.name),
    };
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
          />
          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[90vh] overflow-y-auto bg-bg-card rounded-t-3xl border-t border-line max-w-md mx-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-bg-card border-b border-line/40 px-5 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-emerald-glow" />
                <div>
                  <div className="font-display font-bold text-base leading-tight">Польза</div>
                  {mealName && (
                    <div className="text-xs text-ink-dim">{mealName}</div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-bg-elevated text-ink-muted flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div className="px-5 py-4">
              {loading && (
                <div className="text-center py-8">
                  <div className="inline-block w-6 h-6 border-2 border-emerald-glow border-t-transparent rounded-full animate-spin mb-2" />
                  <div className="text-sm text-ink-muted">Считаю нутриенты…</div>
                </div>
              )}

              {!loading && !data && (
                <div className="text-center py-8 text-ink-muted text-sm">
                  Не удалось загрузить данные
                </div>
              )}

              {!loading && data && (
                <>
                  {/* 🆕 KEY TAKEAWAYS — главные выводы сверху */}
                  {data.key_takeaways && data.key_takeaways.length > 0 && (
                    <div className="mb-5">
                      <div className="text-xs text-emerald-glow font-medium uppercase tracking-wider mb-3">
                        🎯 Что тебе даёт эта еда
                      </div>
                      <div className="space-y-2">
                        {data.key_takeaways.map((tk, idx) => (
                          <div
                            key={idx}
                            className="bg-emerald-glow/5 border border-emerald-glow/20 rounded-xl p-3 flex gap-3"
                          >
                            <div className="text-xl shrink-0 leading-none mt-0.5">{tk.icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-bold text-sm text-ink mb-1">
                                {tk.title}
                              </div>
                              <p className="text-xs text-ink-muted leading-relaxed">
                                {tk.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Вау-отметка (короткая строка, если есть) */}
                  {pending ? (
                    <div className="bg-bg-elevated/50 rounded-xl p-4 mb-4 text-center">
                      <Loader2 className="w-5 h-5 text-emerald-glow animate-spin mx-auto mb-2" aria-hidden />
                      <p className="text-sm text-ink">Считаю витамины и минералы</p>
                      <p className="text-xs text-ink-dim mt-1">
                        {waited < 4
                          ? 'Обычно занимает полминуты — можно не ждать, посчитается само'
                          : 'Дольше обычного. Загляни чуть позже, всё сохранится'}
                      </p>
                    </div>
                  ) : data.wow && (
                    <div className="bg-emerald-glow/10 border border-emerald-glow/30 rounded-xl p-3 mb-4">
                      <p className="text-sm leading-relaxed">{data.wow}</p>
                    </div>
                  )}

                  {/* Таблица нутриентов */}
                  {pending ? null : data.items.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      <div className="text-xs text-ink-dim font-medium mb-2">
                        КЛЮЧЕВЫЕ НУТРИЕНТЫ
                      </div>
                      {data.items.map((rawItem, idx) => {
                        const item = enrichItem(rawItem);
                        const hasSources = item.sources && item.sources.length > 0;

                        return (
                          <div key={idx} className="card p-3">
                            {/* Заголовок: название + уровень */}
                            <div className="flex items-center justify-between mb-1">
                              <div className="font-medium text-sm">{item.name}</div>
                              <div className={`text-[10px] px-2 py-0.5 rounded-full ${LEVEL_COLORS[item.level]}`}>
                                {LEVEL_LABELS[item.level]}
                              </div>
                            </div>

                            {/* Количество + процент */}
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="font-display font-bold text-sm">
                                ~{item.amount} {item.unit}
                              </span>
                              <span className="text-xs text-ink-muted">
                                ({item.daily_percent}% {item.isMacro ? 'твоей нормы' : 'дневной нормы'})
                              </span>
                            </div>

                            {/* Прогресс-бар */}
                            <div className="h-1 bg-line rounded-full overflow-hidden mb-2">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  item.level === 'very_high' ? 'bg-warn' :
                                  item.level === 'excellent' ? 'bg-emerald-glow' :
                                  item.level === 'good' ? 'bg-emerald-glow/70' :
                                  'bg-ink-dim'
                                }`}
                                style={{ width: `${Math.min(100, item.daily_percent)}%` }}
                              />
                            </div>

                            {/* Норма и источник */}
                            {item.normValue && (
                              <div className="flex items-center gap-1.5 mb-2 text-[10px]">
                                {item.isMacro ? (
                                  <span className="text-emerald-glow">
                                    👤 Твоя норма: {item.normValue} {item.unit}/день
                                  </span>
                                ) : (
                                  <span className="text-ink-dim">
                                    {item.sourceLabel === 'Минздрав РФ' ? '🇷🇺' : '🌍'} {item.sourceLabel}: {item.normValue} {item.unit}/день
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Описание нутриента */}
                            <p className="text-[11px] text-ink-muted leading-relaxed mb-2">
                              {item.description}
                            </p>

                            {/* 🆕 РАЗБИВКА ПО ПРОДУКТАМ — сразу видна, мелким шрифтом */}
                            {hasSources && (
                              <div className="mt-2 pt-2 border-t border-line/30 space-y-0.5">
                                {item.sources!.map((src, sidx) => (
                                  <div
                                    key={sidx}
                                    className="flex items-center justify-between text-[10px] text-ink-dim"
                                  >
                                    <span className="truncate flex-1 min-w-0">
                                      ▸ {src.product}{' '}
                                      <span className="text-ink-dim/70">({src.weight_g}г)</span>
                                    </span>
                                    <span className="text-ink-muted font-medium ml-2 shrink-0">
                                      {src.amount} {item.unit}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-ink-muted text-center py-4">
                      Значимых нутриентов в этом блюде не выделено
                    </p>
                  )}

                  {/* Предупреждение */}
                  {data.warning && (
                    <div className="bg-warn/10 border border-warn/30 rounded-xl p-3 mb-4 flex gap-2">
                      <AlertTriangle size={16} className="text-warn shrink-0 mt-0.5" />
                      <p className="text-xs leading-relaxed text-ink">{data.warning}</p>
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div className="bg-bg-elevated rounded-lg p-3 flex gap-2 mb-2">
                    <Info size={14} className="text-ink-dim shrink-0 mt-0.5" />
                    <p className="text-[10px] text-ink-dim leading-relaxed">
                      Данные примерные и зависят от веса порции, способа приготовления и сорта продукта.
                      БЖУ считаются от твоей дневной нормы. Витамины и минералы — от норм Минздрава РФ 2021 и FDA.
                      Не является медицинской рекомендацией.
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Компактный блок-кнопка для отображения на основной карточке
interface NutrientsBadgeProps {
  keywords: string[];
  onClick: () => void;
}

export function NutrientsBadge({ keywords, onClick }: NutrientsBadgeProps) {
  if (!keywords || keywords.length === 0) return null;
  return (
    <button
      onClick={onClick}
      className="w-full mt-3 p-2.5 rounded-lg bg-emerald-glow/5 border border-emerald-glow/20 hover:bg-emerald-glow/10 transition-colors text-left flex items-center justify-between gap-2 group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <Sparkles size={12} className="text-emerald-glow shrink-0" />
          <span className="text-[10px] text-emerald-glow font-medium uppercase tracking-wider">Польза</span>
        </div>
        <div className="text-xs text-ink truncate">
          {keywords.join(' · ')}
        </div>
      </div>
      <span className="text-[11px] text-emerald-glow group-hover:underline shrink-0">
        Подробнее →
      </span>
    </button>
  );
}
